import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { es as dateFnsEs, enUS, de as dateFnsDe, fr as dateFnsFr, it as dateFnsIt, ptBR, nl as dateFnsNl, sv as dateFnsSv, da as dateFnsDa, el as dateFnsEl, fi as dateFnsFi } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

import LoadingOverlayAnimada from '../components/LoadingOverlayAnimada';
import { Helmet } from 'react-helmet';
import CustomDateInput from '../components/CustomDateInput';
import ComoFuncionaSection from '../components/ComoFuncionaSection';
import TiposDeViajeSection from '../components/TiposDeViajeSection';
import SeoHero from '../components/SeoHero';
import Select from 'react-select';
import '../components/HomePageFormOverrides.css';
import './HomePage.css';
import { useModalContext } from '../components/ModalProvider';
import api from '../services/api';
import * as analytics from '../utils/analytics';

// Mapa de locales de date-fns a nivel de módulo (no se recrea en cada render)
// Los imports son directos (no wildcard) para que webpack pueda hacer tree-shaking
const DATE_FNS_LOCALES = {
  es: dateFnsEs,
  en: enUS,
  de: dateFnsDe,
  fr: dateFnsFr,
  it: dateFnsIt,
  pt: ptBR,
  nl: dateFnsNl,
  sv: dateFnsSv,
  da: dateFnsDa,
  el: dateFnsEl,
  fi: dateFnsFi,
};

function HomePage() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const topRef = useRef(null);
  const { showAlert } = useModalContext();
  // Pre-activa el modo Pet-friendly cuando se llega desde un post del blog con ?pet=1
  const [searchParams] = useSearchParams();

  // Sincroniza el idioma de i18n con la URL
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Activa el modo Pet-friendly si la URL trae ?pet=1 (CTA desde el blog)
  useEffect(() => {
    if (searchParams.get('pet') === '1') setPetFriendly(true);
  }, [searchParams]);

  // Obtener la locale de date-fns según el idioma de la URL
  const getDateLocale = () => DATE_FNS_LOCALES[lang || i18n.language] || dateFnsEs;

  const [origen, setOrigen] = useState('');
  const [destino, setDestino] = useState('');
  const [dias, setDias] = useState(1);
  const [personas, setPersonas] = useState(1);
  const [presupuesto, setPresupuesto] = useState('estandar');
  const [ninos, setNinos] = useState(0);
  const [petFriendly, setPetFriendly] = useState(false);
  const [tipoViaje, setTipoViaje] = useState('turismo');
  const [ritmo, setRitmo] = useState('balanced');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  // always use explicit start/end dates
  // defensive flag for any legacy references (we always use explicit dates)
  const usarFechas = true;
  const [cargando, setCargando] = useState(false);
  const [mostrarOverlay, setMostrarOverlay] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const navigate = useNavigate();

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;
    const diffTime = Math.abs(endDate - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  // Convierte Date a string 'yyyy-MM-dd' respetando la zona horaria local
  const toLocalIsoDate = (date) => {
    if (!date) return '';
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split('T')[0];
  };

  // Add useEffect to update days when dates change
  useEffect(() => {
    if (fechaInicio && fechaFin) {
      const calculatedDays = calculateDays(fechaInicio, fechaFin);
      if (calculatedDays > 0) setDias(calculatedDays);
    }
  }, [fechaInicio, fechaFin]);

  // Modify the date input handlers to validate dates
  const handleFechaInicioChange = (e) => {
    const newFechaInicio = e.target.value;
    setFechaInicio(newFechaInicio);
    // Clear fecha fin if it's before the new start date
    if (fechaFin && new Date(fechaFin) < new Date(newFechaInicio)) setFechaFin('');
  };

  const handleFechaFinChange = (e) => {
    const newFechaFin = e.target.value;
    if (fechaInicio) {
      const startDate = new Date(fechaInicio);
      const endDate = new Date(newFechaFin);
      const diffDays = calculateDays(fechaInicio, newFechaFin);

      if (endDate < startDate) {
        showAlert({
          title: 'Error',
          message: t('error_fecha_fin_anterior')
        });
        return;
      }

      if (diffDays > 7) {
        showAlert({
          title: 'Error',
          message: t('error_rango_dias')
        });
        return;
      }
    }

    setFechaFin(newFechaFin);
    if (fechaInicio && newFechaFin) {
      const calculatedDays = calculateDays(fechaInicio, newFechaFin);
      if (calculatedDays > 0) setDias(calculatedDays);
    }
  };

  const calculateMaxDate = (startDate) => {
    if (!startDate) return null;
    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + 6); // Ajustar para incluir solo 7 días en total
    return maxDate;
  };

  const generarItinerario = async () => {
    if (!destino || destino.trim() === '') {
      showAlert({
        title: 'Error',
        message: t('error_destino_requerido')
      });
      return;
    }

    setMostrarOverlay(true);
    setCargando(true);

    const presupuestoMap = { economico: 500, estandar: 1500, premium: 5000 };
    const body = {
      origen,
      destino,
      dias,
      personas,
      ninos,
      isPetFriendly: petFriendly,
      presupuesto: presupuestoMap[presupuesto] ?? 1500,
      tipoViaje,
      ritmo,
    };

  // always send explicit start/end dates
  body.fecha_inicio = fechaInicio;
  body.fecha_fin = fechaFin;

    try {
      const response = await api.post(`/${lang}/itinerario`, body);
      const data = response.data;
      const contenido = data.resultado || t('error_generar_itinerario');
      const parametros = data.parametros || {};

      // 📊 ANALYTICS: Track successful itinerary creation
      analytics.trackItineraryCreated(destino, dias, tipoViaje, ritmo);

      setCargando(false);
      setMostrarOverlay(false);
      navigate(`/${lang}/itinerario`, { state: { contenido, parametros } });
    } catch (error) {
      setCargando(false);
      setMostrarOverlay(false);
      console.error('❌ Error generando itinerario:', error);
      
      // Obtener mensaje de error más específico
      let errorMsg = t('error_generar_itinerario', 'No se pudo generar el itinerario.');
      
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.status === 500) {
        errorMsg = 'Error del servidor. Por favor verifica los logs de Laravel.';
      } else if (error.response?.status === 503) {
        errorMsg = 'El servicio de IA no está disponible. Por favor intenta más tarde.';
      } else if (error.response?.status === 504) {
        errorMsg = 'La solicitud tardó demasiado tiempo. Por favor intenta de nuevo.';
      } else if (error.message === 'Network Error') {
        errorMsg = 'Error de conexión. Verifica que Laravel esté corriendo en http://localhost:8000';
      }
      
      showAlert({
        title: t('error', 'Error'),
        message: errorMsg
      });
    }
  };

  // Optimized SEO metadata for improved CTR and search ranking
  const ogImage = 'https://planiaro.com/og-image-planiaro.jpg';
  const getHomepageSeoContent = () => {
    // Prefer lang from URL params (always matches the actual page) over i18n.language
    // which may briefly lag behind during react-snap pre-rendering.
    const currentLang = lang || i18n.language || 'es';
    // ALWAYS use production domain — never window.location (breaks during prerendering)
    const origin = 'https://planiaro.com';
    const baseUrl = `${origin}/${currentLang}/`;
    
    const content = {
      es: {
        title: 'Planificador de Viajes con IA GRATIS — Itinerario en 60 seg | Planiaro',
        description: '⚡ Crea itinerarios IA personalizados en 60 segundos. La IA analiza 1000+ destinos, optimiza rutas y presupuesto. Sin registro, 100% gratis. Probado por +10.000 viajeros desde México, España y Latinoamérica',
        url: baseUrl,
        keywords: 'planificador viajes IA gratis, generador itinerarios automático, crear itinerario viaje, planificar viaje con IA, ia viajes gratis, itinerario viaje personalizado'
      },
      en: {
        title: 'Free AI Travel Planner — Full itinerary in 60 sec, no sign-up | Planiaro',
        description: 'Free AI travel planner: get a complete day-by-day itinerary with hotels, activities and daily budget in 60 seconds. No sign-up, no credit card. Try it now →',
        url: baseUrl,
        keywords: 'free AI travel planner, AI trip planner, itinerary generator, create itinerary online free, AI travel planner no credit card, trip itinerary maker'
      },
      de: {
        title: 'Wie plane ich eine Reise mit KI? — Kostenloser Reiseplaner 2026 | Planiaro',
        description: 'Reiseplanung mit KI in 4 Schritten: Ziel & Budget eingeben, KI erstellt deinen kompletten Reiseplan mit Hotels & Aktivitäten. Kostenlos, ohne Anmeldung →',
        url: baseUrl,
        keywords: 'ki reiseplaner kostenlos, kostenloser ki reiseplaner, ki reiseplanung kostenlos, ki routenplanung kostenlos, reiseplan erstellen, reise ki kostenlos'
      },
      fr: {
        title: 'Planificateur de voyage gratuit avec IA — Itinéraire en 60 sec | Planiaro',
        description: "Planificateur de voyage gratuit : créez un itinéraire jour par jour avec hôtels, activités et budget en 60 secondes grâce à l'IA. Sans inscription, sans carte bancaire. Essayez maintenant →",
        url: baseUrl,
        keywords: 'planificateur de voyage gratuit, planificateur de voyage ia gratuit, créer un itinéraire de voyage gratuit, ia voyage gratuit, générateur itinéraires automatique'
      },
      it: {
        title: 'Pianificatore di Viaggi IA Gratuito — Itinerario completo in 60 sec, senza registrazione | Planiaro',
        description: 'Pianificatore di viaggi IA gratuito: ricevi un itinerario giorno per giorno con hotel, attività e budget in 60 secondi. Senza registrazione, senza carta di credito. Provalo ora →',
        url: baseUrl,
        keywords: 'pianificatore di viaggi ia gratuito, generatore itinerari di viaggio, ottimizzazione percorsi con ia, creare itinerario viaggio gratis, pianificatore automatico viaggi'
      },
      pt: {
        title: 'Planejador de Viagens com IA GRÁTIS — Crie Roteiro em 60 seg | Planiaro',
        description: 'Gere itinerários de viagem personalizados instantaneamente com IA. Otimize orçamento, datas e atividades. Sem cadastro, 100% grátis!',
        url: baseUrl,
        keywords: 'planejador viagem IA grátis, gerador itinerários automático, criar roteiro viagem, planejamento IA'
      },
      nl: {
        title: 'Gratis AI Reisplanner — Compleet reisplan in 60 sec | Planiaro',
        description: 'Gratis AI reisplanner: ontvang een compleet dag-per-dag reisplan met hotels, activiteiten en dagelijks budget in 60 seconden. Zonder registratie, zonder creditcard. Probeer nu →',
        url: baseUrl,
        keywords: 'gratis ai reisplanner, ai reisplanner, reisplanner maken, reisplan maken, AI reisplanning'
      },
      sv: {
        title: 'AI Reseplanerare gratis — Komplett reseplan på 60 sek, utan registrering | Planiaro',
        description: 'Gratis AI reseplanerare: få en komplett dag-för-dag reseplan med hotell, aktiviteter och daglig budget på 60 sekunder. Utan registrering, utan kreditkort. Prova nu →',
        url: baseUrl,
        keywords: 'ai reseplanerare, gratis AI reseplanerare, rese generator automatisk, skapa reseplan, AI resplanering'
      },
      da: {
        title: 'Rejseplanlægger med AI — Gratis rejseplan på 60 sek | Planiaro',
        description: 'Gratis AI rejseplanlægger: få en komplet dag-for-dag rejseplan med hoteller, aktiviteter og dagligt budget på 60 sekunder. Uden tilmelding, uden kreditkort. Prøv nu →',
        url: baseUrl,
        keywords: 'rejseplanlægger, gratis AI rejseplanlægger, rejse generator automatisk, lave rejseplan, AI rejseplanlægning'
      },
      el: {
        title: 'ΔΩΡΕΑΝ Σχεδιαστής Ταξιδιών AI — Δημιουργήστε σε 60 δευτ | Planiaro',
        description: 'Δημιουργήστε εξατομικευμένα σχέδια ταξιδιών άμεσα με AI. Βελτιστοποιήστε budget, ημερομηνίες και δραστηριότητες. Χωρίς εγγραφή, 100% δωρεάν!',
        url: baseUrl,
        keywords: 'δωρεάν AI σχεδιαστής ταξιδιών, γεννήτρια δρομολογίων αυτόματη, δημιουργία σχεδίου, AI προγραμματισμός'
      },
      fi: {
        title: 'ILMAINEN AI Matkasuunnittelija — Luo Matkasi 60 sek | Planiaro',
        description: '✨ Luo henkilökohtaiset matkasuunnitelmat välittömästi AI:lla. Optimoi budjetti, päivämäärät ja aktiviteetit. Ei rekisteröitymistä, 100% ilmainen!',
        url: baseUrl,
        keywords: 'ilmainen AI matkasuunnittelija, matka generaattori automaattinen, luo matkasuunnitelma, AI matkasuunnittelu'
      }
    };
    
    return content[currentLang] || content.es;
  };

  const homepageSeo = getHomepageSeoContent();

  // FAQ for HomePage (transactional questions)
  const homeFaqKeys = [
    { q: 'homepage.faq.q1.q', a: 'homepage.faq.q1.a' },
    { q: 'homepage.faq.q2.q', a: 'homepage.faq.q2.a' },
    { q: 'homepage.faq.q3.q', a: 'homepage.faq.q3.a' },
    { q: 'homepage.faq.q4.q', a: 'homepage.faq.q4.a' },
    { q: 'homepage.faq.q5.q', a: 'homepage.faq.q5.a' },
    { q: 'homepage.faq.q6.q', a: 'homepage.faq.q6.a' },
    { q: 'homepage.faq.q7.q', a: 'homepage.faq.q7.a' },
    { q: 'homepage.faq.q8.q', a: 'homepage.faq.q8.a' },
    { q: 'homepage.faq.q9.q', a: 'homepage.faq.q9.a' },
    { q: 'homepage.faq.q10.q', a: 'homepage.faq.q10.a' },
  ];
  const homeFaqList = homeFaqKeys
    .map(k => ({ q: t(k.q), a: t(k.a) }))
    .filter(item => !item.q.startsWith('homepage.faq'));

  return (
    <>
      <Helmet>
        <title>{homepageSeo.title}</title>
        <meta name="description" content={homepageSeo.description} />
        <meta name="keywords" content={homepageSeo.keywords} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="language" content={lang || 'es'} />
        <link rel="canonical" href={homepageSeo.url} />
        <meta property="og:title" content={homepageSeo.title} />
        <meta property="og:description" content={homepageSeo.description} />
        <meta property="og:url" content={homepageSeo.url} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Planiaro" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Planiaro - Planificador de viajes con IA gratis" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={homepageSeo.title} />
        <meta name="twitter:description" content={homepageSeo.description} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:image:alt" content="Generador de itinerarios de viaje con inteligencia artificial" />
        <meta name="author" content="Planiaro" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Planiaro - AI Travel Planner",
          "description": homepageSeo.description,
          "url": homepageSeo.url,
          "applicationCategory": "TravelApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Planiaro",
          "url": homepageSeo.url,
          "description": "AI-powered travel itinerary planner",
          "logo": "https://planiaro.com/logo_planiaro.png",
          "sameAs": ["https://www.facebook.com/planiaro", "https://www.twitter.com/planiaro", "https://www.instagram.com/planiaro"]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": homeFaqList.map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.a
            }
          }))
        })}</script>
      </Helmet>
      <div ref={topRef}></div>
      <LoadingOverlayAnimada visible={mostrarOverlay} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div className="hero-area">
          <div className="container" style={{ maxWidth: 1000, margin: '0 auto', paddingTop: 10, paddingBottom: 70 }}>
              <div style={{ position: 'relative', marginBottom: 6, zIndex: 2 }}>
              <h1 className="text-center mb-1 fw-bold" style={{ fontFamily: "'Poppins', 'Poppins Fallback', sans-serif", color: '#ffffff', fontSize: 'clamp(28px, 4.5vw, 44px)', fontWeight: 800, letterSpacing: '-0.01em', textTransform: 'none', margin: 0 }}>
                {lang === 'es' ? 'Planificador de Viajes con IA Gratis' : lang === 'en' ? '🌍 Free AI Travel Planner' : lang === 'de' ? '🌍 Kostenloser KI-Reiseplaner' : lang === 'fr' ? '🌍 Planificateur de Voyage IA Gratuit' : lang === 'it' ? '🌍 Generatore di Itinerari IA' : lang === 'pt' ? '🌍 Planejador de Viagens com IA' : t('bienvenido')}
              </h1>
              <p className="text-center" style={{ fontFamily: "'Inter', 'Inter Fallback', sans-serif", color: 'rgba(255,255,255,0.85)', marginTop: 10, fontSize: 16, fontWeight: 400 }}>{t('itinerario_personalizado')}</p>
            </div>

            {/* Form first to give it more prominence */}
            <div className="form-container homepage-form-container" style={{ maxWidth: 1100, background: '#ffffff', border: 'none', padding: '1.25rem 1.5rem', marginTop: 32, marginBottom: 36, position: 'relative', zIndex: 3, boxShadow: '0 20px 50px rgba(76,29,149,0.25)' }}>
            <form>
              {/* ROW 1: Origen/Destino + Fechas */}
              <div className="form-section-label">
                <span className="form-section-icon"><i className="bi bi-geo-alt-fill"></i></span> {t('donde_y_cuando', '¿Dónde y cuándo?')}
              </div>
              <div className="form-row-section" style={{ marginBottom: 16 }}>
                {/* Origen + Destino combined */}
                <div className="field-group" style={{ minWidth: 150, flex: 1 }}>
                  <div className="origen-destino-labels" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <label htmlFor="origen">{t('origen')}</label>
                    <label htmlFor="destino">{t('destino')}</label>
                  </div>
                  <div className="origen-destino-wrapper" style={{ display: 'flex', gap: 0, alignItems: 'stretch', overflow: 'hidden', maxHeight: 40 }}>
                    <div style={{ flex: 1, padding: '6px 6px', minWidth: 0 }}>
                      <input id="origen" type="text" className="form-control" placeholder={t('origen', 'Origen del viaje')} value={origen} onChange={e => setOrigen(e.target.value)} style={{ borderRadius: 0, border: 'none', paddingLeft: 6, height: 32 }} />
                    </div>
                    <div className="origen-destino-separator" aria-hidden="true" />
                    <div style={{ flex: 1, padding: '6px 6px', minWidth: 0 }}>
                      <input id="destino" type="text" className="form-control" placeholder={t('destino', 'Destino del viaje')} value={destino} onChange={e => setDestino(e.target.value)} style={{ borderRadius: 0, border: 'none', paddingLeft: 6, height: 32 }} />
                    </div>
                  </div>
                </div>
                {/* Fechas */}
                <div className="field-group fechas-row" style={{ minWidth: 300, flex: 1 }}>
                  <div className="origen-destino-labels" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <label>{t('fecha_inicio')}</label>
                    <label>{t('fecha_final')}</label>
                  </div>
                  <div className="origen-destino-wrapper" style={{ display: 'flex', gap: 0, alignItems: 'stretch', overflow: 'hidden', maxHeight: 40 }}>
                    <div style={{ flex: 1, padding: '6px 6px', minWidth: 0 }}>
                      <DatePicker
                        selected={fechaInicio ? new Date(fechaInicio) : null}
                        onChange={date => {
                          const formattedDate = date ? toLocalIsoDate(date) : '';
                          handleFechaInicioChange({ target: { value: formattedDate } });
                        }}
                        locale={getDateLocale()}
                        dateFormat="dd/MM/yyyy"
                        customInput={<CustomDateInput />}
                        placeholderText={t('fecha_inicio')}
                        minDate={new Date()}
                        maxDate={fechaFin ? new Date(fechaFin) : null}
                        popperPlacement="bottom-start"
                        popperClassName="datepicker-popper-homepage"
                      />
                    </div>
                    <div className="origen-destino-separator" aria-hidden="true" />
                    <div style={{ flex: 1, padding: '6px 6px', minWidth: 0 }}>
                      <DatePicker
                        selected={fechaFin ? new Date(fechaFin) : null}
                        onChange={date => {
                          const formattedDate = date ? toLocalIsoDate(date) : '';
                          handleFechaFinChange({ target: { value: formattedDate } });
                        }}
                        locale={getDateLocale()}
                        dateFormat="dd/MM/yyyy"
                        customInput={<CustomDateInput />}
                        placeholderText={t('fecha_final')}
                        minDate={fechaInicio ? new Date(fechaInicio) : new Date()}
                        maxDate={calculateMaxDate(fechaInicio)}
                        popperPlacement="bottom-start"
                        popperClassName="datepicker-popper-homepage"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ROW 2: Tipo de viaje, Ritmo, Presupuesto */}
              <div className="form-section-label">
                <span className="form-section-icon"><i className="bi bi-sliders"></i></span> {t('personaliza_viaje', 'Personaliza tu viaje')}
              </div>
              <div className="form-row-section">
                {/* Tipo de viaje */}
                <div className="field-group" style={{ minWidth: 140, flex: 1 }}>
                  <label id="label-tipoViaje" htmlFor="tipoViaje">{t('tipo_viaje')}</label>
                  <Select
                    id="tipoViaje"
                    instanceId="tipoViaje"
                    aria-labelledby="label-tipoViaje"
                    options={[
                      { value: 'turismo', label: t('turismo') },
                      { value: 'aventura', label: t('aventura') },
                      { value: 'naturaleza', label: t('naturaleza') },
                      { value: 'gastronómico', label: t('gastronomico') }
                    ]}
                    value={tipoViaje ? {
                      value: tipoViaje,
                      label: [
                        { value: 'turismo', label: t('turismo') },
                        { value: 'aventura', label: t('aventura') },
                        { value: 'naturaleza', label: t('naturaleza') },
                        { value: 'gastronómico', label: t('gastronomico') }
                      ].find(opt => opt.value === tipoViaje)?.label
                    } : null}
                    onChange={opt => setTipoViaje(opt.value)}
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: 38,
                        borderRadius: 12,
                        borderColor: state.isFocused ? '#7531FC' : '#e8e0ff',
                        borderWidth: '1.5px',
                        boxShadow: state.isFocused ? '0 0 0 3px rgba(117,49,252,0.10)' : 'none',
                        background: '#fff',
                        fontSize: 14,
                        fontWeight: 500,
                        paddingLeft: 2,
                        paddingRight: 2,
                        transition: 'all 0.2s ease'
                      }),
                      menu: base => ({
                        ...base,
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 12px 32px 0 rgba(107,48,234,0.15)',
                        border: '1px solid #e8e0ff',
                        overflow: 'hidden'
                      }),
                      menuPortal: base => ({ ...base, zIndex: 99999 }),
                      option: (base, state) => ({
                        ...base,
                        background: state.isSelected ? '#7531FC' : state.isFocused ? '#f3efff' : '#fff',
                        color: state.isSelected ? '#fff' : '#1a1033',
                        fontWeight: state.isSelected ? 600 : 400,
                        fontSize: 14,
                        padding: '10px 14px',
                        transition: 'all 0.15s ease'
                      })
                    }}
                    isSearchable={false}
                  />
                </div>
                {/* Ritmo */}
                <div className="field-group" style={{ minWidth: 120, flex: 1 }}>
                  <label id="label-ritmo" htmlFor="ritmo">{t('ritmo')}</label>
                  <Select
                    id="ritmo"
                    instanceId="ritmo"
                    aria-labelledby="label-ritmo"
                    options={[
                      { value: 'relaxed', label: t('relajado') },
                      { value: 'balanced', label: t('equilibrado') },
                      { value: 'intense', label: t('intenso') }
                    ]}
                    value={ritmo ? {
                      value: ritmo,
                      label: [
                        { value: 'relaxed', label: t('relajado') },
                        { value: 'balanced', label: t('equilibrado') },
                        { value: 'intense', label: t('intenso') }
                      ].find(opt => opt.value === ritmo)?.label
                    } : null}
                    onChange={opt => setRitmo(opt.value)}
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: 38,
                        borderRadius: 12,
                        borderColor: state.isFocused ? '#7531FC' : '#e8e0ff',
                        borderWidth: '1.5px',
                        boxShadow: state.isFocused ? '0 0 0 3px rgba(117,49,252,0.10)' : 'none',
                        background: '#fff',
                        fontSize: 14,
                        fontWeight: 500,
                        paddingLeft: 2,
                        paddingRight: 2,
                        transition: 'all 0.2s ease'
                      }),
                      menu: base => ({
                        ...base,
                        zIndex: 1056,
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 12px 32px 0 rgba(107,48,234,0.15)',
                        border: '1px solid #e8e0ff',
                        overflow: 'hidden'
                      }),
                      menuPortal: base => ({ ...base, zIndex: 99999 }),
                      option: (base, state) => ({
                        ...base,
                        background: state.isSelected ? '#7531FC' : state.isFocused ? '#f3efff' : '#fff',
                        color: state.isSelected ? '#fff' : '#1a1033',
                        fontWeight: state.isSelected ? 600 : 400,
                        fontSize: 14,
                        padding: '10px 14px',
                        transition: 'all 0.15s ease'
                      })
                    }}
                    isSearchable={false}
                  />
                </div>
                {/* Presupuesto */}
                <div className="field-group" style={{ minWidth: 130, flex: 1 }}>
                  <label id="label-presupuesto">{t('presupuesto', 'Presupuesto')}</label>
                  <Select
                    id="presupuesto"
                    instanceId="presupuesto"
                    aria-labelledby="label-presupuesto"
                    options={[
                      { value: 'economico', label: t('presupuesto_economico', 'Económico') },
                      { value: 'estandar',  label: t('presupuesto_estandar', 'Estándar') },
                      { value: 'premium',   label: t('presupuesto_premium', 'Premium') },
                    ]}
                    value={{ value: presupuesto, label: presupuesto === 'economico' ? t('presupuesto_economico', 'Económico') : presupuesto === 'premium' ? t('presupuesto_premium', 'Premium') : t('presupuesto_estandar', 'Estándar') }}
                    onChange={opt => setPresupuesto(opt.value)}
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: 38,
                        borderRadius: 12,
                        borderColor: state.isFocused ? '#7531FC' : '#e8e0ff',
                        borderWidth: '1.5px',
                        boxShadow: state.isFocused ? '0 0 0 3px rgba(117,49,252,0.10)' : 'none',
                        background: '#fff',
                        fontSize: 14,
                        fontWeight: 500,
                        paddingLeft: 2,
                        paddingRight: 2,
                        transition: 'all 0.2s ease'
                      }),
                      menu: base => ({
                        ...base,
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 12px 32px 0 rgba(107,48,234,0.15)',
                        border: '1px solid #e8e0ff',
                        overflow: 'hidden'
                      }),
                      menuPortal: base => ({ ...base, zIndex: 99999 }),
                      option: (base, state) => ({
                        ...base,
                        background: state.isSelected ? '#7531FC' : state.isFocused ? '#f3efff' : '#fff',
                        color: state.isSelected ? '#fff' : '#1a1033',
                        fontWeight: state.isSelected ? 600 : 400,
                        fontSize: 14,
                        padding: '10px 14px',
                        transition: 'all 0.15s ease'
                      })
                    }}
                    isSearchable={false}
                  />
                </div>
                {/* Personas */}
                <div className="field-group" style={{ minWidth: 140, flex: 1 }}>
                  <label htmlFor="personas">{t('n_viajeros')}</label>
                  <div className="personas-display">
                    <button
                      type="button"
                      className="stepper-btn"
                      aria-label={t('restar_viajero')}
                      onClick={() => setPersonas(prev => Math.max(1, prev - 1))}
                      tabIndex={-1}
                    >−</button>
                    <span className="personas-value">
                      {personas} {personas === 1 ? t('viajero') : t('viajeros')}
                    </span>
                    <button
                      type="button"
                      className="stepper-btn"
                      aria-label={t('sumar_viajero')}
                      onClick={() => setPersonas(prev => Math.min(10, prev + 1))}
                      tabIndex={-1}
                    >+</button>
                  </div>
                </div>
              </div>

              {/* ROW 3: Niños, Pet-friendly, Botón */}
              <div className="form-row-section" style={{ alignItems: 'flex-end', marginTop: 20 }}>
                {/* Niños + Pet-friendly grouped together */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 28, flex: '0 0 auto' }}>
                  {/* Niños — toggle switch */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38 }}>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={ninos === 1}
                      onClick={() => setNinos(ninos === 1 ? 0 : 1)}
                      style={{
                        position: 'relative',
                        width: 48,
                        height: 26,
                        borderRadius: 13,
                        border: 'none',
                        background: ninos === 1 ? '#7531FC' : '#d1c4e9',
                        cursor: 'pointer',
                        transition: 'background 0.2s ease',
                        flexShrink: 0,
                        padding: 0,
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        top: 3,
                        left: ninos === 1 ? 25 : 3,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: '#fff',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                        transition: 'left 0.2s ease',
                        display: 'block',
                      }} />
                    </button>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#1a1033' }}>
                      {t('n_ninos', 'Niños')}
                    </span>
                  </div>
                  {/* Pet-friendly — toggle switch */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38 }}>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={petFriendly}
                      onClick={() => setPetFriendly(prev => !prev)}
                      style={{
                        position: 'relative',
                        width: 48,
                        height: 26,
                        borderRadius: 13,
                        border: 'none',
                        background: petFriendly ? '#7531FC' : '#d1c4e9',
                        cursor: 'pointer',
                        transition: 'background 0.2s ease',
                        flexShrink: 0,
                        padding: 0,
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        top: 3,
                        left: petFriendly ? 25 : 3,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: '#fff',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                        transition: 'left 0.2s ease',
                        display: 'block',
                      }} />
                    </button>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#1a1033' }}>
                      🐾 {t('pet_friendly', 'Pet-friendly')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Botón: última fila, ancho completo */}
              <div style={{ marginTop: 20 }}>
                <button type="button" onClick={generarItinerario} disabled={cargando} className="btn-generate">
                  <span className="btn-icon"><i className="bi bi-stars"></i></span>
                  {cargando ? t('generando') : t('generar_viaje')}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>

        

  {/* SECCIONES INFORMATIVAS MODERNAS */}
  <div className="home-sections-container">
    <div className="container" style={{ maxWidth: 1600, margin: '0 auto', paddingLeft: 16, paddingRight: 16 }}>

      {/* SEO HERO SECTION */}
      <div className="modern-section" style={{ marginTop: 0 }}>
        <SeoHero />
      </div>

      {/* STATS BANNER */}
      <div className="stats-grid">
        {[
          { icon: 'bi-journal-check', value: '15.000+', labelKey: 'homepage.stats.itinerarios', labelFb: 'Itinerarios generados' },
          { icon: 'bi-map',           value: '1.000+',  labelKey: 'homepage.stats.destinos',   labelFb: 'Destinos cubiertos' },
          { icon: 'bi-translate',     value: '11',       labelKey: 'homepage.stats.idiomas',    labelFb: 'Idiomas disponibles' },
          { icon: 'bi-gift',          value: '100%',     labelKey: 'homepage.stats.gratis',     labelFb: 'Gratis, sin registro' },
        ].map(({ icon, value, labelKey, labelFb }) => (
          <div key={icon} className="stat-item">
            <div className="stat-icon">
              <i className={`bi ${icon}`}></i>
            </div>
            <div className="stat-value">{value}</div>
            <div className="stat-label">{t(labelKey, labelFb)}</div>
          </div>
        ))}
      </div>

      {/* CÓMO FUNCIONA SECTION */}
      <div className="modern-section">
        <ComoFuncionaSection />
      </div>

      {/* DESTINOS POPULARES — CTAs para generar itinerario, NO links a blog */}
      <div className="modern-section">
        <div className="section-header-modern">
          <h2>{t('homepage.destinos.titulo', 'Genera tu itinerario para estos destinos')}</h2>
          <p style={{ color: '#666', marginTop: 8, fontSize: 16 }}>{t('homepage.destinos.subtitulo', 'Haz clic en un destino y la IA creará tu plan de viaje personalizado en 60 segundos')}</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 14, marginTop: 24 }}>
          {[
            { emoji: '🏛️', name: 'Roma',      days: 3 },
            { emoji: '🗼', name: 'París',     days: 5 },
            { emoji: '🏖️', name: 'Barcelona', days: 5 },
            { emoji: '🌊', name: 'Lisboa',    days: 5 },
            { emoji: '🚲', name: 'Ámsterdam', days: 3 },
            { emoji: '🎨', name: 'Madrid',    days: 3 },
          ].map(dest => (
            <button
              key={dest.name}
              type="button"
              onClick={() => {
                setDestino(dest.name);
                topRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{ background: 'white', borderRadius: 14, padding: '20px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1.5px solid #e8e0ff', textAlign: 'center', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer', width: '100%' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(117,49,252,0.15)'; e.currentTarget.style.borderColor = '#7531FC'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'; e.currentTarget.style.borderColor = '#e8e0ff'; }}
            >
              <div style={{ fontSize: 34, marginBottom: 8 }}>{dest.emoji}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1033', marginBottom: 6 }}>{dest.name}</div>
              <div style={{ fontSize: 11, color: '#7531FC', fontWeight: 600, background: '#f5f0ff', borderRadius: 8, padding: '2px 8px', display: 'inline-block' }}>✨ {t('generar_viaje', 'Generar')}</div>
            </button>
          ))}
        </div>
      </div>

      {/* FEATURES SECTION — Planifica con IA + Por qué IA, unified */}
      <div className="modern-section">
        <div className="section-header-modern">
          <h2>{t('planifica_ia', 'Planifica tu viaje con IA')}</h2>
          <p>{t('por_que_ia_desc', 'Nuestra IA convierte tus preferencias en un plan de viaje práctico y detallado, optimizando tiempo y presupuesto.')}</p>
        </div>
        <div className="features-ia-grid">
          {[
            {
              icon: 'bi-sliders2',
              title: t('feat.personalizado.title', 'Totalmente personalizado'),
              sub:   t('feat.personalizado.sub',   'Presupuesto, ritmo, tipo de viaje y número de viajeros'),
            },
            {
              icon: 'bi-lightning-charge',
              title: t('feat.rapido.title', 'Listo en segundos'),
              sub:   t('feat.rapido.sub',   'La IA genera tu itinerario completo sin esperas'),
            },
            {
              icon: 'bi-geo-alt',
              title: t('feat.local.title', 'Recomendaciones locales'),
              sub:   t('feat.local.sub',   'Actividades, restaurantes y rutas optimizadas para cada destino'),
            },
            {
              icon: 'bi-calendar-check',
              title: t('feat.flexible.title', 'Flexible y adaptable'),
              sub:   t('feat.flexible.sub', 'Perfecto para escapadas de fin de semana o vacaciones largas'),
            },
            {
              icon: 'bi-stopwatch',
              title: t('feat.tiempo.title', 'Ahorra tiempo'),
              sub:   t('feat.tiempo.sub',   'Sin horas de investigación: rutas completas al instante'),
            },
            {
              icon: 'bi-compass',
              title: t('feat.estilo.title', 'Según tu estilo'),
              sub:   t('feat.estilo.sub',   'Desde turismo cultural hasta aventura o gastronomía'),
            },
            {
              icon: 'bi-check2-circle',
              title: t('feat.estres.title', 'Sin estrés'),
              sub:   t('feat.estres.sub',   'Nos ocupamos de toda la organización del viaje por ti'),
            },
            {
              icon: 'bi-translate',
              title: t('feat.idiomas.title', '11 idiomas'),
              sub:   t('feat.idiomas.sub',   'Disponible en español, inglés, alemán, francés y más'),
            },
          ].map(({ icon, title, sub }) => (
            <div key={icon} className="feature-ia-item">
              <div className="feature-ia-icon">
                <i className={`bi ${icon}`}></i>
              </div>
              <div className="feature-ia-text">
                <span className="feature-ia-title">{title}</span>
                <span className="feature-ia-sub">{sub}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* TIPOS DE VIAJE SECTION */}
      <div className="modern-section">
        <TiposDeViajeSection />
      </div>

      {/* BLOG — solo 2 posts sobre IA, no directorio de destinos */}
      <div className="modern-section" style={{ textAlign: 'center' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem' }}>
          🤖 {t('como_usar_ia_viaje', '¿Cómo usar la IA para planificar tu viaje?')}
        </h2>
        <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '2rem' }}>
          {t('aprende_planificar_ia', 'Guías prácticas para sacar el máximo partido al planificador de viajes con IA')}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '16px', maxWidth: 700, margin: '16px auto 0' }}>
          {[
            {
              slug: 'como-planificar-un-viaje-paso-a-paso-con-ayuda-de-la-ia',
              variants: { en: 'how-to-plan-a-trip-step-by-step-with-ai', de: 'wie-man-eine-reise-schritt-fuer-schritt-mit-ki-plant', fr: 'comment-planifier-un-voyage-etape-par-etape-avec-lia', it: 'come-pianificare-un-viaggio-passo-dopo-passo-con-lia', pt: 'como-planejar-uma-viagem-passo-a-passo-com-ajuda-de-ia', nl: 'hoe-plan-je-een-reis-stap-voor-stap-met-ai-hulp', sv: 'hur-du-planerar-en-resa-steg-fuer-steg-med-hjaelp-av-ai', da: 'saadan-planlægger-du-en-rejse-trin-for-trin-med-ai-hjaelp', fi: 'kuinka-suunnitella-matka-vaihe-vaiheelta-tekoaelyn-avulla', el: 'pos-na-sxediaseis-taksidi-vima-vima-me-texniti-noimosini' },
              title: t('blog_ia_planificar_title', '📋 Cómo planificar tu viaje con IA paso a paso'),
              desc: t('blog_ia_planificar_desc', 'Aprende a usar Planiaro para generar el itinerario perfecto en minutos'),
            },
            {
              slug: 'el-mejor-planificador-de-viajes-ia-gratis',
              variants: { en: 'the-best-ai-trip-planner-free', de: 'der-beste-ki-reiseplaner-kostenlos', fr: 'le-meilleur-planificateur-de-voyage-ia-gratuit', it: 'il-miglior-pianificatore-di-viaggio-ia-gratuito', pt: 'o-melhor-planejador-de-viagens-com-ia-gratuito', nl: 'de-beste-ai-reisplanner-gratis', sv: 'basta-ai-reseplaneraren-gratis', da: 'bedste-ai-rejseplanlægger-gratis', fi: 'paras-ai-matkasuunnittelija-ilmainen', el: 'kalyteroi-dorean-ai-sxediasts-taxidiou' },
              title: t('blog_ia_mejor_title', '⭐ Por qué Planiaro es el mejor planificador de viajes gratuito'),
              desc: t('blog_ia_mejor_desc', 'Comparativa de herramientas y por qué la IA supera a los planificadores tradicionales'),
            },
          ].map(post => {
            const finalSlug = (lang && lang !== 'es' && post.variants[lang]) ? post.variants[lang] : post.slug;
            return (
              <Link key={post.slug} to={`/${lang || 'es'}/blog/${finalSlug}/`} style={{ textDecoration: 'none' }}>
                <div
                  style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transition: 'transform 0.2s', cursor: 'pointer', textAlign: 'left', border: '1.5px solid #f0edff' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = '#7531FC'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#f0edff'; }}
                >
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1033', marginBottom: '0.5rem' }}>{post.title}</h3>
                  <p style={{ color: '#666', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>{post.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* FAQ SECTION */}
      <div className="modern-section" style={{ textAlign: 'left' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '1.5rem', textAlign: 'center' }}>
          {t('homepage.faq.title', '❓ Preguntas frecuentes sobre Planiaro')}
        </h2>
        <div className="faq-grid">
          {homeFaqList.map((f, idx) => {
            const isOpen = openFaqIndex === idx;
            return (
              <div key={idx} className="faq-item">
                <button
                  type="button"
                  className="faq-question"
                  aria-expanded={isOpen}
                  onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                >
                  <span>{f.q}</span>
                  <i className={`bi bi-chevron-down faq-chevron ${isOpen ? 'faq-chevron--open' : ''}`}></i>
                </button>
                <div className={`faq-answer-wrap ${isOpen ? 'faq-answer-wrap--open' : ''}`}>
                  <p className="faq-answer" aria-hidden={!isOpen}>{f.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CTA FINAL SECTION */}
      <div className="cta-section-modern">
        <h2>{t('listo_proximo_viaje')}</h2>
        <p>{t('deja_ia')}</p>
        <button
          onClick={() => {
            topRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
          disabled={cargando}
          className="transporte-btn-add"
          style={{ fontSize: 18, minWidth: 240, margin: '20px auto 0' }}
        >
          {cargando ? t('generando') : t('planifica_gratis')}
        </button>
      </div>

    </div>
  </div>
      </div>
    </>
  );
}

export default HomePage;